// =============================================================================
// store/modules/schedule.js
// =============================================================================
// Vuex module.  Register in store/index.js:
//
//   import schedule from './modules/schedule'
//   modules: { ..., schedule }
//
// Follows the exact same action / mutation / error pattern as device.js.
// =============================================================================

import ApiService from '@/services/api.service'
import { Mutation } from '@/store/mutations'   // adjust path to your project

// --- mutation names (add these to your central mutations file) ---
// Mutation.schedule.set        = 'schedule/SET'
// Mutation.schedule.setSummary = 'schedule/SET_SUMMARY'
// Mutation.schedule.setActive  = 'schedule/SET_ACTIVE'

// --- action names (add these to your central actions file) ---
// Action.schedule.load         = 'schedule/load'
// Action.schedule.loadSummary  = 'schedule/loadSummary'
// Action.schedule.get          = 'schedule/get'
// Action.schedule.create       = 'schedule/create'
// Action.schedule.update       = 'schedule/update'
// Action.schedule.cancel       = 'schedule/cancel'
// Action.schedule.triggerDevice = 'schedule/triggerDevice'

const state = {
  schedules: [],       // list view — active schedules
  summary: null,       // { pending, ready, in_progress, expired, failed, completed, needs_attention }
  activeSchedule: null // detail view — single schedule with device_entries
}

const mutations = {
  ['schedule/SET'](state, schedules) {
    state.schedules = schedules
  },
  ['schedule/SET_SUMMARY'](state, summary) {
    state.summary = summary
  },
  ['schedule/SET_ACTIVE'](state, schedule) {
    state.activeSchedule = schedule
  },
}

const actions = {
  // Load list
  async ['schedule/load'](context, status = 'active') {
    try {
      const result = await ApiService.schedule.all(status)
context.commit('schedule/SET', Array.isArray(result.data) ? result.data : (result.data?.data || []))
    } catch (error) {
      // silent — same pattern as device.load
    }
  },

  // Load badge summary
  async ['schedule/loadSummary'](context) {
    try {
      const result = await ApiService.schedule.summary()
      context.commit('schedule/SET_SUMMARY', result.data)
    } catch (error) {
      // silent
    }
  },

  // Load single schedule (detail page)
  async ['schedule/get'](context, scheduleId) {
    try {
      const result = await ApiService.schedule.get(scheduleId)
      context.commit('schedule/SET_ACTIVE', result.data)
      return result.data
    } catch (error) {
      context.commit(Mutation.notifyApiError, error)
      return Promise.reject(error)
    }
  },

  // Create
  async ['schedule/create'](context, data) {
    try {
      const result = await ApiService.schedule.create(data)
      context.commit(Mutation.notify, {
        type: 'success',
        title: 'Schedule Created',
        message: `"${data.name}" is now active`,
      })
      return result.data
    } catch (error) {
      context.commit(Mutation.notifyApiError, error)
      return Promise.reject(error)
    }
  },

  // Edit
  async ['schedule/update'](context, { scheduleId, data }) {
    try {
      const result = await ApiService.schedule.update(scheduleId, data)
      context.commit(Mutation.notify, {
        type: 'success',
        title: 'Schedule Updated',
        message: 'Changes saved',
      })
      return result.data
    } catch (error) {
      context.commit(Mutation.notifyApiError, error)
      return Promise.reject(error)
    }
  },

  // Cancel
  async ['schedule/cancel'](context, scheduleId) {
    try {
      await ApiService.schedule.cancel(scheduleId)
      context.commit(Mutation.notify, {
        type: 'success',
        title: 'Schedule Cancelled',
        message: 'Schedule has been cancelled',
      })
      // Reload list
      context.dispatch('schedule/load')
    } catch (error) {
      context.commit(Mutation.notifyApiError, error)
      return Promise.reject(error)
    }
  },

  // Trigger one device (manual mode)
  async ['schedule/triggerDevice'](context, { scheduleId, deviceId }) {
    try {
      const result = await ApiService.schedule.triggerDevice(scheduleId, deviceId)
      context.commit(Mutation.notify, {
        type: 'success',
        title: 'Update Triggered',
        message: `Update started for device ${deviceId}`,
      })
      return result.data
    } catch (error) {
      context.commit(Mutation.notifyApiError, error)
      return Promise.reject(error)
    }
  },
}

const getters = {
  schedules: state => state.schedules,
  scheduleSummary: state => state.summary,
  activeSchedule: state => state.activeSchedule,
  needsAttention: state => state.summary?.needs_attention || 0,
}

export default {
  state,
  mutations,
  actions,
  getters,
}